import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, ListChecks } from 'lucide-react'
import { flattenTourSteps, type TourDoc } from './tourTypes'
import type { TourEngine } from './useTourEngine'
import type { TokenResolveOptions } from './tourTokens'
import { renderTokenText } from './tourTokens'

/**
 * Storylane-style checklist over the running tour: a collapsed pill showing
 * progress, expanding to the item list. An item is complete once its target
 * step has been VIEWED (not merely jumped past — viewing is the thing the
 * checklist promises), and clicking an incomplete item drives the engine
 * there. Resume seeding: on mount, every step before the current one counts
 * as viewed, so a returning visitor's checklist matches what they saw.
 *
 * Rendering note: this sits inside the `.fg-tour` root, which is
 * pointer-events:none by design — the wrapper class opts back in. See
 * fg-tour__leadform for the scar tissue behind that rule.
 */
export function TourChecklist({ doc, engine, tokens }: {
  doc: TourDoc
  engine: TourEngine
  tokens: TokenResolveOptions
}) {
  const checklist = doc.checklist
  const steps = useMemo(() => flattenTourSteps(doc), [doc])
  const [open, setOpen] = useState(false)
  const currentId = engine.current?.step.id ?? null
  const currentIndex = engine.current?.index ?? 0

  const seeded = useRef(false)
  const [viewed, setViewed] = useState<Set<string>>(() => new Set())
  useEffect(() => {
    setViewed((prev) => {
      const next = new Set(prev)
      if (!seeded.current) {
        seeded.current = true
        for (const ref of steps) if (ref.index < currentIndex) next.add(ref.step.id)
      }
      if (currentId) next.add(currentId)
      return next
    })
  }, [currentId, currentIndex, steps])

  if (!checklist || checklist.items.length === 0) return null
  const t = (text: string) => renderTokenText(text, tokens)
  const done = checklist.items.filter((item) => viewed.has(item.stepId)).length

  return (
    <div className="fg-tour__checklist">
      <button
        type="button"
        className="fg-tour__checklist-pill"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <ListChecks size={14} aria-hidden />
        {t(checklist.title)} · {done}/{checklist.items.length}
      </button>
      {open ? (
        <ul className="fg-tour__checklist-panel">
          {checklist.items.map((item) => {
            const complete = viewed.has(item.stepId)
            return (
              <li key={item.id}>
                <button
                  type="button"
                  className={`fg-tour__checklist-item${complete ? ' is-done' : ''}`}
                  aria-label={complete ? `${item.label} — done` : `${item.label} — go to this step`}
                  onClick={() => {
                    engine.emitChecklistItem(item.id, item.stepId)
                    engine.gotoStep(item.stepId)
                    setOpen(false)
                  }}
                >
                  <span className="fg-tour__checklist-tick" aria-hidden>
                    {complete ? <Check size={12} /> : null}
                  </span>
                  {t(item.label)}
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
